/**
 * Utility functions for custom field value transformations
 */

import { 
  CustomFieldDependencyLevel, 
  CustomFieldOption 
} from '../hooks/useCompanyCustomFields';

/**
 * Converts dependent field value from internal format to backend API format
 * 
 * Internal format: { level_id: option_id }
 * Example: { 88: 857, 89: 859 }
 * 
 * Backend format: Dictionary with string keys { "88": 857, "89": 859 }
 */
export const convertDependentFieldForSubmission = (
  value: { [levelId: number]: number } | null | undefined,
  dependencyLevels: CustomFieldDependencyLevel[],
  allOptions: CustomFieldOption[]
): any => {
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
    return {};
  }

  // Convert number keys to string keys for backend
  const result: { [key: string]: number } = {};
  
  Object.keys(value).forEach(levelIdStr => {
    const levelId = Number(levelIdStr);
    const optionId = value[levelId];
    
    // Verify this level exists
    const levelExists = dependencyLevels.some(
      l => l.custom_field_dependency_level_id === levelId
    );
    
    // Verify this option exists
    const optionExists = allOptions.some(
      o => o.custom_field_choice_option_id === optionId
    );
    
    if (levelExists && optionExists) {
      result[levelId.toString()] = optionId;
    }
  });

  return result;
};

/**
 * Converts dependent field value from backend API format to internal format
 * 
 * Backend format: Dictionary { "88": 857, "89": 859 } or Array of levels (old format)
 * Internal format: { level_id: option_id }
 */
export const convertDependentFieldFromAPI = (
  apiValue: any
): { [levelId: number]: number } | null => {
  if (!apiValue) {
    return null;
  }

  // If it's already a dictionary/object (new format)
  if (typeof apiValue === 'object' && !Array.isArray(apiValue)) {
    const result: { [levelId: number]: number } = {};
    
    Object.keys(apiValue).forEach(key => {
      const levelId = Number(key);
      const optionId = Number(apiValue[key]);
      
      if (!isNaN(levelId) && !isNaN(optionId)) {
        result[levelId] = optionId;
      }
    });
    
    return Object.keys(result).length > 0 ? result : null;
  }

  // If it's an array (old format) - for backward compatibility
  if (Array.isArray(apiValue)) {
    const result: { [levelId: number]: number } = {};

    apiValue.forEach(level => {
      if (level.level_id && level.options && Array.isArray(level.options)) {
        const selectedOption = level.options.find((opt: any) => opt.is_selected === true);
        if (selectedOption && selectedOption.option_id) {
          result[level.level_id] = selectedOption.option_id;
        }
      }
    });

    return Object.keys(result).length > 0 ? result : null;
  }

  return null;
};

/**
 * Prepares custom field values for API submission
 * Handles both regular fields and dependent fields
 */
export const prepareCustomFieldsForSubmission = (
  customFieldValues: { [fieldId: number]: any },
  customFields: any[],
  dependencyLevelsMap: { [fieldId: number]: CustomFieldDependencyLevel[] },
  optionsMap: { [fieldId: number]: CustomFieldOption[] }
) => {
  const result: any[] = [];

  Object.keys(customFieldValues).forEach(fieldIdStr => {
    const fieldId = Number(fieldIdStr);
    const value = customFieldValues[fieldId];
    const field = customFields.find(f => f.custom_field_id === fieldId);

    if (!field || value === null || value === undefined || value === '') {
      return; // Skip empty values
    }

    const isDependentField = field.custom_field_data_type?.custom_field_data_type_key === 'DEPENDENT_FIELD';

    if (isDependentField) {
      // Convert dependent field value
      const dependencyLevels = dependencyLevelsMap[fieldId] || [];
      const options = optionsMap[fieldId] || [];
      
      const convertedValue = convertDependentFieldForSubmission(
        value,
        dependencyLevels,
        options
      );

      if (Object.keys(convertedValue).length > 0) {
        result.push({
          custom_field_id: fieldId,
          custom_field_value: convertedValue
        });
      }
    } else {
      // Regular field - pass as-is
      result.push({
        custom_field_id: fieldId,
        custom_field_value: value
      });
    }
  });

  return result;
};

/**
 * Parses custom field values from API response
 * Handles both regular fields and dependent fields
 */
export const parseCustomFieldsFromAPI = (
  apiCustomFields: any[] | null | undefined
): { [fieldId: number]: any } => {
  if (!apiCustomFields || !Array.isArray(apiCustomFields)) {
    return {};
  }

  const result: { [fieldId: number]: any } = {};

  apiCustomFields.forEach(field => {
    if (field.custom_field_id && field.custom_field_value !== undefined) {
      const value = field.custom_field_value;
      
      if (typeof value === 'object' && value !== null) {
        const convertedValue = convertDependentFieldFromAPI(value);
        if (convertedValue) {
          result[field.custom_field_id] = convertedValue;
        } else {
          result[field.custom_field_id] = value;
        }
      } else {
        result[field.custom_field_id] = value;
      }
    }
  });

  return result;
};

const normalizeValue = (value: any): any => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (Array.isArray(value) && value.length === 0) {
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return null;
  }
  return value;
};

export const customFieldValuesAreEqual = (value1: any, value2: any): boolean => {
  const normalized1 = normalizeValue(value1);
  const normalized2 = normalizeValue(value2);
  
  if (normalized1 === null && normalized2 === null) {
    return true;
  }
  
  return JSON.stringify(normalized1) === JSON.stringify(normalized2);
};

